import { useCallback } from "react";

import {
    isValidPublicKey,
    useAbfFetches,
    useLoopscaleWalletContext,
    useVerifyTwitterFollowMutation
} from "@bridgesplit/abf-react";
import { useSearchParams } from "react-router-dom";
import { Result, formatAddress, getReadableErrorMessage, parseErrorFromOptions } from "@bridgesplit/utils";
import { mutationIntoRawResult, useAsyncResultHandler, useAppNavigate } from "@bridgesplit/react";
import { useOnInitialRenderRef } from "@bridgesplit/ui";
import { WAITLIST_SLUG } from "app/constants";

const DEFAULT_ERROR = getReadableErrorMessage("verify with X");
const CONNECTION_CANCELLED = "X connection was cancelled";
const skipSentry = true;
export default function TwitterCallback() {
    const [params] = useSearchParams();
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const { resetPoints } = useAbfFetches();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const [verifyTwitterFollow] = useVerifyTwitterFollowMutation();

    const navigate = useAppNavigate();
    const { resultHandler, skipHook } = useAsyncResultHandler();

    const onFinish = useCallback(() => navigate(WAITLIST_SLUG), [navigate]);
    const skipRequest = skipHook || !activeWallet;

    const verify = useCallback(async () => {
        if (error) {
            const message = error === "access_denied" ? CONNECTION_CANCELLED : `X authorization failed: ${error}`;
            return Result.errFromMessage(message, { skipSentry });
        }
        if (!code || !state) return Result.errFromMessage("No X credentials provided", { skipSentry });

        const verifyFollowRes = await mutationIntoRawResult(verifyTwitterFollow, { code, state }, { skipSentry });

        if (verifyFollowRes.isOk()) {
            resetPoints();
            return verifyFollowRes;
        }

        const rawError = verifyFollowRes.unwrapErrMessage();
        if (rawError.includes(DUPLICATE_ERROR)) {
            const parsedWallet = parseDuplicateWallet(rawError);
            if (parsedWallet) {
                return Result.errFromMessage(
                    `This X account is already connected to a different wallet: ${formatAddress(parsedWallet)}`,
                    { skipSentry }
                );
            }
        }
        const parsedError = parseErrorFromOptions(rawError, TWITTER_ERRORS);
        return Result.errFromMessage(parsedError ?? DEFAULT_ERROR, { skipSentry });
    }, [code, error, resetPoints, state, verifyTwitterFollow]);

    useOnInitialRenderRef(
        () =>
            resultHandler(verify, {
                alertOnError: true,
                onFail: onFinish,
                onSuccess: onFinish,
                onSuccessMessage: "X account connected"
            }),
        skipRequest
    );

    return null;
}

function parseDuplicateWallet(input: string): string | null {
    const match = input.match(/"([^"]*)"/);
    if (!match || !match[1]) return null;

    if (isValidPublicKey(match[1])) return match[1];
    return null;
}

const DUPLICATE_ERROR = "User or handle already marked as followed by wallet";
const TWITTER_ERRORS = {
    "is not followed by the user": "You aren't following @Loopscale on X",
    "User not logged into twitter!": "Unable to verify your account with X servers",
    "Invalid log-in from Twitter": "Unable to verify your account with X servers",
    "OAuth token does not match": "Unable to verify your account with X servers",
    "User or handle already marked as followed": "This X account is already connected to a different Loopscale account"
};
